import { createTriggerResource } from '@magicdoorinc/ui';
import { createEffect, createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { DEFAULT_RECONCILIATION_WORK_QUEUE_STATUSES } from '~/pages/bank-accounts/bank-account-details/reconciliation/reconciliationApiTypes';
import {
  defaultOccurredStartDate,
  isValidOccurredDateRange,
} from '~/pages/bank-accounts/bank-account-details/reconciliation/reconciliationWorkspaceHelpers';
import { reconciliationsRepository } from '~/repositories/reconciliationsRepository';
import type {
  GetItemsParams,
  ReconciliationItemDetailDto,
  ReconciliationItemStatus,
  ReconciliationResolutionType,
  ReconciliationReviewReason,
} from '~/swagger/accounting/Api';

export type ReconciliationItemFilters = {
  statuses?: ReconciliationItemStatus[];
  resolutionTypes?: ReconciliationResolutionType[];
  reviewReasons?: ReconciliationReviewReason[];
  occurredStart?: string;
  occurredEnd?: string;
};

const defaultItemFilters = (): ReconciliationItemFilters => ({
  statuses: [...DEFAULT_RECONCILIATION_WORK_QUEUE_STATUSES],
  occurredStart: defaultOccurredStartDate(),
});

export const [ReconciliationWorkspaceProvider, useReconciliationWorkspace] = createMagicDoorContext('ReconciliationWorkspace', () => {
  const [bankAccountId, setBankAccountId] = createSignal<string>();
  const [itemFilters, setItemFilters] = createSignal<ReconciliationItemFilters>(defaultItemFilters());

  const [connections, loadConnections, connectionsActions] = createTriggerResource(async (_scope: true) =>
    reconciliationsRepository.getConnections()
  );

  const [summary, loadSummary, summaryActions] = createTriggerResource(async (accountId: string) =>
    reconciliationsRepository.getSummary({ bankAccountId: accountId })
  );

  const [items, loadItems, itemsActions] = createTriggerResource(async (params: GetItemsParams) =>
    reconciliationsRepository.getItems(params)
  );

  const [expandedItemIds, setExpandedItemIds] = createSignal<Set<string>>(new Set());
  const [detailCache, setDetailCache] = createSignal<Record<string, ReconciliationItemDetailDto>>({});
  const [loadingDetailIds, setLoadingDetailIds] = createSignal<Set<string>>(new Set());

  createEffect(() => {
    const id = bankAccountId();
    if (!id) return;
    setItemFilters(defaultItemFilters());
    loadConnections(true);
    loadSummary(id);
  });

  const buildItemsParams = (page: number, pageSize: number): GetItemsParams | undefined => {
    const id = bankAccountId();
    if (!id) return;
    const filters = itemFilters();
    const params: GetItemsParams = {
      bankAccountId: id,
      page,
      pageSize,
      statuses: filters.statuses,
      resolutionTypes: filters.resolutionTypes,
      reviewReasons: filters.reviewReasons,
    };
    if (filters.occurredStart) params['occurred.start'] = filters.occurredStart;
    if (filters.occurredEnd) params['occurred.end'] = filters.occurredEnd;
    return params;
  };

  const fetchItems = (page: number, pageSize: number) => {
    const params = buildItemsParams(page, pageSize);
    if (!params) return;
    if (!isValidOccurredDateRange(params['occurred.start'], params['occurred.end'])) return;
    loadItems(params);
  };

  const reload = () => {
    connectionsActions.refetch();
  };

  const invalidateItems = () => {
    itemsActions.refetch();
  };

  const invalidateSummary = () => {
    summaryActions.refetch();
  };

  const invalidateItemsAndSummary = () => {
    invalidateItems();
    invalidateSummary();
  };

  const reloadAll = () => {
    connectionsActions.refetch();
    invalidateItemsAndSummary();
  };

  const resetItemFilters = () => {
    setItemFilters(defaultItemFilters());
  };

  const patchItemFilters = (patch: ReconciliationItemFilters) => {
    setItemFilters(patch);
  };

  const toggleExpanded = async (itemId: string) => {
    const current = new Set(expandedItemIds());
    if (current.has(itemId)) {
      current.delete(itemId);
      setExpandedItemIds(current);
      return;
    }

    if (detailCache()[itemId]) {
      current.add(itemId);
      setExpandedItemIds(current);
      return;
    }

    setLoadingDetailIds((ids) => new Set(ids).add(itemId));
    current.add(itemId);
    setExpandedItemIds(current);

    try {
      const detail = await reconciliationsRepository.getItem(itemId);
      setDetailCache((cache) => ({ ...cache, [itemId]: detail }));
    } finally {
      setLoadingDetailIds((ids) => {
        const next = new Set(ids);
        next.delete(itemId);
        return next;
      });
    }
  };

  const cacheDetail = (itemId: string, detail: ReconciliationItemDetailDto) => {
    setDetailCache((cache) => ({ ...cache, [itemId]: detail }));
  };

  const collapseItem = (itemId: string) => {
    setExpandedItemIds((ids) => {
      const next = new Set(ids);
      next.delete(itemId);
      return next;
    });
    setDetailCache((cache) => {
      const next = { ...cache };
      delete next[itemId];
      return next;
    });
  };

  return {
    bankAccountId,
    setBankAccountId,
    summary,
    summaryLoading: () => summary.loading,
    items,
    itemsLoading: () => items.loading,
    fetchItems,
    itemFilters,
    setItemFilters: patchItemFilters,
    resetItemFilters,
    connections,
    reload,
    reloadAll,
    invalidateItems,
    invalidateSummary,
    invalidateItemsAndSummary,
    expandedItemIds,
    toggleExpanded,
    collapseItem,
    detailCache,
    loadingDetailIds,
    cacheDetail,
  };
});
